import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, HStack, Text, IconButton, Avatar, Badge, Tooltip, Button, keyframes } from "@chakra-ui/react";
import { Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, Mic, MicOff, Pause, Play, Move, X, ExternalLink, Power } from "lucide-react";
import { useSoftphone, STATUS_FINAIS } from "../contexts/softphone";

const WIDGET_WIDTH = 300;
const WIDGET_HEIGHT_MINI = 56;

const STATUS_BADGE = (status, heartbeatOk) => {
    if (status === "registrado" && heartbeatOk) return { label: "Online", color: "green" };
    if (status === "registrado") return { label: "Verificando", color: "yellow" };
    if (status === "conectando" || status === "conectado") return { label: "Conectando", color: "yellow" };
    return { label: "Offline", color: "gray" };
};

const fmtDuration = (sec) => {
    if (!sec && sec !== 0) return "00:00";
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
};

const fmtNumero = (raw) => {
    const n = String(raw || "").replace(/\D/g, "");
    if (n.length === 11) return `(${n.slice(0,2)}) ${n.slice(2,7)}-${n.slice(7)}`;
    if (n.length === 10) return `(${n.slice(0,2)}) ${n.slice(2,6)}-${n.slice(6)}`;
    return raw || "";
};

const pulseRing = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
    70%  { box-shadow: 0 0 0 14px rgba(16, 185, 129, 0); }
    100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`;

const SoftphoneWidget = () => {
    const sp = useSoftphone();
    const navigate = useNavigate();
    const location = useLocation();
    const widgetRef = useRef(null);
    const dragStateRef = useRef(null);
    const sizeRef = useRef({ w: WIDGET_WIDTH, h: WIDGET_HEIGHT_MINI });

    // Posição preferida (salva/arrastada). Pode ter sido salva em outro monitor,
    // zoom ou janela maior — por isso NUNCA é usada crua: ver `shown` abaixo.
    // Sem posição salva, o widget nascia colado no rodapé (innerHeight - 80) e
    // caía em cima da barra fixa de Salvar/Publicar da /ura — na primeira vez
    // que alguém abria a tela, o botão Publicar estava coberto. Mais acima
    // também sai da frente do rodapé de notificações (bottom 20px).
    const [pos, setPos] = useState(() => {
        const x = sp.config?.widget_posicao?.x;
        const y = sp.config?.widget_posicao?.y;
        return {
            x: x ?? (window.innerWidth - WIDGET_WIDTH - 16),
            y: y ?? Math.max(80, window.innerHeight - 160),
        };
    });
    const [hidden, setHidden] = useState(false);
    const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });
    const [size, setSize] = useState({ w: WIDGET_WIDTH, h: WIDGET_HEIGHT_MINI });

    useEffect(() => {
        const x = sp.config?.widget_posicao?.x;
        const y = sp.config?.widget_posicao?.y;
        if (x != null && y != null) setPos({ x, y });
    }, [sp.config?.widget_posicao?.x, sp.config?.widget_posicao?.y]);

    const inCall = !!sp.call && !STATUS_FINAIS.includes(sp.call.status);
    const incoming = inCall && sp.call.direction === "incoming" && !sp.call.answeredAt;
    const ativo = inCall && sp.call.answeredAt;

    // Quando chega chamada entrante, força widget visível mesmo se usuário tinha ocultado.
    useEffect(() => {
        if (incoming) setHidden(false);
    }, [incoming]);

    const modo = sp.config?.modo_recepcao;
    const noDiscador = location.pathname === "/discador";

    // Regra de visibilidade:
    // - Em /discador, nunca mostra widget (a tela já cobre).
    // - Sem credenciais SIP, nunca mostra.
    // - Se há chamada entrante, mostra SEMPRE (independente de hidden/modo).
    // - Caso contrário, respeita hidden e modo_recepcao.
    const visivel = !noDiscador && sp.credentials && (
        incoming
        || (!hidden && (modo === "widget_global" || modo === "ambos"))
        || ativo // chamada em andamento — não pode sumir o controle
    );

    // Viewport atual (redimensionar janela, trocar de monitor, zoom).
    useEffect(() => {
        const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Tamanho REAL do widget: o painel de chamada recebida é ~4x mais alto que a
    // barra mini, então uma posição válida pra barra pode jogar os botões
    // Atender/Recusar pra fora da tela quando a chamada chega.
    useEffect(() => {
        const el = widgetRef.current;
        if (!el) return;
        const medir = () => {
            const s = { w: el.offsetWidth || WIDGET_WIDTH, h: el.offsetHeight || WIDGET_HEIGHT_MINI };
            sizeRef.current = s;
            setSize((prev) => (prev.w === s.w && prev.h === s.h ? prev : s));
        };
        medir();
        if (typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(medir);
        ro.observe(el);
        return () => ro.disconnect();
    }, [visivel, incoming, ativo, inCall]);

    // Posição efetivamente exibida: a preferida, puxada pra dentro da tela.
    // Chamado 11/09/2026: posição salva x=2100 num monitor grande deixava o widget
    // (e o botão Atender) invisível em tela menor — só o banner vermelho aparecia.
    const shown = {
        x: Math.max(0, Math.min(viewport.w - size.w, pos.x)),
        y: Math.max(0, Math.min(viewport.h - size.h, pos.y)),
    };

    // Espelho da posição para o fim do arrasto, que grava no servidor.
    const posRef = useRef(pos);
    useEffect(() => { posRef.current = pos; }, [pos]);

    // Arrastar por Pointer Events: com mousedown/mousemove o widget era IMÓVEL
    // no celular — e durante chamada ativa o X de ocultar não aparece, então
    // widget em cima de um controle virava beco sem saída. O pointer capture
    // manda os eventos seguintes para o próprio cabeçalho: sem listener global
    // e sem reassinar nada a cada pixel (o efeito antigo dependia de `pos` e do
    // `sp`, que muda a cada tique do cronômetro).
    const onPointerDown = useCallback((e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        // Botão do cabeçalho (abrir tela cheia, ocultar) não arrasta.
        if (e.target?.closest?.("button")) return;
        dragStateRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            origX: shown.x,
            origY: shown.y,
            moveu: false,
        };
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* navegador antigo */ }
        document.body.style.userSelect = "none";
    }, [shown.x, shown.y]);

    const onPointerMove = useCallback((e) => {
        const st = dragStateRef.current;
        if (!st || st.pointerId !== e.pointerId) return;
        const dx = e.clientX - st.startX;
        const dy = e.clientY - st.startY;
        // Folga para o toque: sem isto, um tique no cabeçalho já "arrasta".
        if (!st.moveu && Math.abs(dx) + Math.abs(dy) < 4) return;
        st.moveu = true;
        const { w, h } = sizeRef.current;
        setPos({
            x: Math.max(0, Math.min(window.innerWidth - w, st.origX + dx)),
            y: Math.max(0, Math.min(window.innerHeight - h, st.origY + dy)),
        });
    }, []);

    const onPointerUp = useCallback((e) => {
        const st = dragStateRef.current;
        if (!st || st.pointerId !== e.pointerId) return;
        dragStateRef.current = null;
        document.body.style.userSelect = "";
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* já solto */ }
        // Só grava se saiu do lugar: antes, qualquer clique no cabeçalho
        // mandava um POST de configuração.
        if (st.moveu) sp.saveConfig({ widget_posicao: { x: posRef.current.x, y: posRef.current.y } });
    }, [sp]);

    if (!visivel) return null;

    const badge = STATUS_BADGE(sp.status, sp.heartbeatOk);
    const numeroExibir = sp.call?.remote || "";
    const nomeExibir = sp.activeCliente?.nome || fmtNumero(numeroExibir) || numeroExibir;

    return (
        <Box
            ref={widgetRef}
            position="fixed"
            left={`${shown.x}px`}
            top={`${shown.y}px`}
            width={`${WIDGET_WIDTH}px`}
            zIndex={2000}
            bg="white"
            borderRadius="xl"
            boxShadow={incoming ? "2xl" : "lg"}
            border="2px solid"
            borderColor={incoming ? "green.400" : ativo ? "blue.300" : "gray.200"}
            overflow="hidden"
            animation={incoming ? `${pulseRing} 1.5s infinite` : undefined}
            transition="all 0.2s"
        >
            {/* Header / Drag handle */}
            <HStack
                px={2} py={1.5}
                bg={incoming ? "green.50" : "gray.50"}
                borderBottom="1px solid"
                borderColor="gray.100"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                cursor="grab"
                // O dedo no cabeçalho arrasta o widget; sem isto o navegador
                // rola a página no meio do arrasto.
                sx={{ touchAction: "none" }}
                spacing={2}
            >
                <Move size={12} color="#94a3b8" />
                <Phone size={14} color={incoming ? "#10b981" : (badge.color === "green" ? "#10b981" : "#94a3b8")} />
                <Badge colorScheme={badge.color} fontSize="9px" borderRadius="sm">{badge.label}</Badge>
                {sp.credentials?.numbersip && !incoming && (
                    <Text fontSize="10px" color="gray.500" fontFamily="mono">{sp.credentials.numbersip}</Text>
                )}
                <Box flex={1} />
                <Tooltip label="Abrir tela completa">
                    <IconButton size="xs" variant="ghost" icon={<ExternalLink size={12} />} onClick={() => navigate("/discador")} aria-label="abrir" />
                </Tooltip>
                {!incoming && !ativo && (
                    <Tooltip label="Ocultar até nova chamada">
                        <IconButton size="xs" variant="ghost" icon={<X size={12} />} onClick={() => setHidden(true)} aria-label="ocultar" />
                    </Tooltip>
                )}
            </HStack>

            {/* Conteúdo */}
            {incoming ? (
                <Box p={4} bg="green.50">
                    <Box textAlign="center" mb={3}>
                        <HStack justify="center" mb={2}>
                            <PhoneIncoming size={14} color="#10b981" />
                            <Text fontSize="10px" color="green.700" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
                                Chamada Recebida
                            </Text>
                        </HStack>
                        <Avatar size="lg" name={nomeExibir} mb={2} bg="green.500" color="white" />
                        <Text fontSize="md" fontWeight="bold" color="gray.800" noOfLines={1}>{nomeExibir}</Text>
                        {sp.activeCliente && (
                            <Text fontSize="xs" color="gray.500">{fmtNumero(numeroExibir)}</Text>
                        )}
                    </Box>
                    <HStack spacing={2}>
                        <Button flex={1} size="md" colorScheme="green" leftIcon={<Phone size={16} />} onClick={sp.answer} h="44px" fontWeight="bold">
                            Atender
                        </Button>
                        <Button flex={1} size="md" colorScheme="red" leftIcon={<PhoneOff size={16} />} onClick={sp.hangup} h="44px" fontWeight="bold">
                            Recusar
                        </Button>
                    </HStack>
                </Box>
            ) : ativo ? (
                <Box p={3}>
                    <HStack mb={2}>
                        {sp.call.direction === "incoming"
                            ? <PhoneIncoming size={14} color="#10b981" />
                            : <PhoneOutgoing size={14} color="#3b82f6" />
                        }
                        <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="semibold">Em chamada</Text>
                        <Text fontSize="11px" color="gray.700" fontFamily="mono" ml="auto">{fmtDuration(sp.call.durationSec)}</Text>
                    </HStack>
                    <HStack mb={3}>
                        <Avatar size="sm" name={nomeExibir} />
                        <Box minW={0}>
                            <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>{nomeExibir}</Text>
                            {sp.activeCliente && <Text fontSize="10px" color="gray.500" noOfLines={1}>{fmtNumero(numeroExibir)}</Text>}
                        </Box>
                    </HStack>
                    <HStack>
                        <Tooltip label={sp.call.muted ? "Ativar mic" : "Silenciar"}>
                            <IconButton size="sm" variant="outline" colorScheme={sp.call.muted ? "red" : "gray"} icon={sp.call.muted ? <MicOff size={14} /> : <Mic size={14} />} onClick={sp.toggleMute} aria-label="mute" />
                        </Tooltip>
                        <Tooltip label={sp.call.onHold ? "Retomar" : "Pausar"}>
                            <IconButton size="sm" variant="outline" colorScheme={sp.call.onHold ? "yellow" : "gray"} icon={sp.call.onHold ? <Play size={14} /> : <Pause size={14} />} onClick={sp.toggleHold} aria-label="hold" />
                        </Tooltip>
                        <Button flex={1} size="sm" colorScheme="red" leftIcon={<PhoneOff size={14} />} onClick={sp.hangup}>Encerrar</Button>
                    </HStack>
                </Box>
            ) : inCall ? (
                <Box p={3} textAlign="center">
                    <Text fontSize="10px" color="gray.500" textTransform="uppercase" fontWeight="semibold" mb={2}>
                        {sp.call.status === "discando" ? "Discando" : "Chamando"}
                    </Text>
                    <Avatar size="md" name={nomeExibir} mb={2} />
                    <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>{nomeExibir}</Text>
                    <Button mt={3} size="sm" colorScheme="red" leftIcon={<PhoneOff size={14} />} onClick={sp.hangup} w="100%">Cancelar</Button>
                </Box>
            ) : (
                <HStack p={2} spacing={2}>
                    {sp.registered ? (
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={sp.disconnect} leftIcon={<Power size={12} />}>Desconectar</Button>
                    ) : (
                        <Button size="xs" variant="ghost" colorScheme="green" onClick={sp.connect} leftIcon={<Power size={12} />} isDisabled={!sp.credentials}>Conectar</Button>
                    )}
                    <Box flex={1} />
                    <Button size="xs" variant="outline" colorScheme="blue" leftIcon={<Phone size={12} />} onClick={() => navigate("/discador")}>
                        Discar
                    </Button>
                </HStack>
            )}
        </Box>
    );
};

export default SoftphoneWidget;
